import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";

/**
 * Inline breadcrumb crumbs, rendered inside TopBar (single header row). Shows
 * L1 / L2 / (L3 · <container|external>) and jumps between levels. The legend
 * and the L3 data-view switch live on the canvas now, not here.
 */
export function Breadcrumb() {
  const { state, dispatch } = useArchViz();
  if (!state.architecture) return null;

  const container = state.selectedContainerId
    ? state.architecture.containers.find((c) => c.id === state.selectedContainerId)
    : null;
  const external = state.selectedExternalId
    ? state.architecture.externalSystems.find((e) => e.id === state.selectedExternalId)
    : null;

  const crumbs: { label: string; level: 1 | 2 | 3; active: boolean }[] = [
    { label: "L1 · System Context", level: 1, active: state.drillLevel === 1 },
    { label: "L2 · Containers", level: 2, active: state.drillLevel === 2 },
  ];
  if (container) {
    crumbs.push({ label: `L3 · ${container.label}`, level: 3, active: state.drillLevel === 3 });
  } else if (external) {
    crumbs.push({
      label: `L3 · External · ${external.label}`,
      level: 3,
      active: state.drillLevel === 3,
    });
  }

  const showEscHint = state.drillLevel > 1 || state.selectedNodeId !== null;

  return (
    <div className="flex items-center gap-1 min-w-0 text-sm">
      {crumbs.map((crumb, i) => (
        <div
          key={crumb.label}
          className={cn("flex items-center gap-1", !crumb.active && "max-md:hidden")}
        >
          {i > 0 && <span className="text-xs text-ink-subtle select-none max-md:hidden">/</span>}
          <button
            type="button"
            title={crumb.label}
            onClick={() => dispatch({ type: "JUMP_TO_LEVEL", level: crumb.level })}
            disabled={crumb.active}
            className={cn(
              "px-2.5 py-1 rounded-md transition-colors duration-base ease-out-soft truncate max-w-[220px]",
              crumb.active
                ? "bg-brand-50 text-ink-heading font-semibold"
                : "text-ink-muted hover:bg-surface-tint hover:text-ink",
            )}
          >
            {crumb.label}
          </button>
        </div>
      ))}
      {showEscHint && (
        <span className="text-[10px] text-muted-foreground flex items-center gap-1 ml-1 max-md:hidden">
          <kbd className="px-1 rounded bg-muted">Esc</kbd> up
        </span>
      )}
    </div>
  );
}
